import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps a tab group's active id in a URL search param so tabs are linkable/shareable.
 *
 * The param value IS the tab id (already a kebab slug, e.g. "appointed-carriers").
 * `activeTabId` = the param value when it matches a known tab id (case-insensitive),
 * otherwise `defaultTabId`. It recomputes as `tabIds` grows (dynamic tab lists), so a
 * param that is not valid yet (carriers still loading) becomes active once its tab appears.
 *
 * Pages with more than one tab group pass a distinct `param` per group. Writes build from
 * the live `window.location.search` (which `navigate` updates synchronously) instead of the
 * render snapshot, so concurrent param writes in the same tick compose instead of clobbering.
 *
 * When `writeDefaultToUrl` is true (default), the default is written to the URL on mount if
 * the param is absent — so arriving from a link/sidebar leaves the active tab explicit in the
 * URL. Set it false when the consumer writes the param itself (e.g. a smart default).
 *
 * `active` (default true) is for a child tab group whose visibility depends on a parent tab:
 * when false, the param is dropped from the URL (and no default is written), so switching the
 * parent away clears the secondary param.
 *
 * `correctInvalidToDefault` (default false) replaces a present-but-unknown param with the
 * resolved default in the URL, so a bad/typo'd tab doesn't linger. Only safe for STATIC tab
 * lists — leave it false for dynamic lists (e.g. carrier parent-groups), where a param can be
 * "not valid yet" and become valid once the data loads.
 *
 * @param {{ param: string, tabIds: string[], defaultTabId: string, writeDefaultToUrl?: boolean, active?: boolean, correctInvalidToDefault?: boolean }} options
 * @returns {[string, (id: string | null) => void]} [activeTabId, setActiveTabId]
 */
export default function useTabUrlState({
  param,
  tabIds,
  defaultTabId,
  writeDefaultToUrl = true,
  active = true,
  correctInvalidToDefault = false
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);

  const matched =
    raw && tabIds.find(id => id.toLowerCase() === raw.toLowerCase());
  const activeTabId = matched || defaultTabId;

  const setActiveTabId = useCallback(
    id => {
      const next = new URLSearchParams(window.location.search);
      if (id) next.set(param, id);
      else next.delete(param);
      setSearchParams(next, { replace: true });
    },
    [param, setSearchParams]
  );

  useEffect(() => {
    // child group hidden by its parent tab → drop its param from the URL
    if (!active) {
      if (!raw) return;
      const next = new URLSearchParams(window.location.search);
      next.delete(param);
      setSearchParams(next, { replace: true });
      return;
    }
    if (raw) {
      // param present but not a known tab → replace it with the default (static lists only)
      if (!correctInvalidToDefault || matched) return;
      const next = new URLSearchParams(window.location.search);
      next.set(param, defaultTabId);
      setSearchParams(next, { replace: true });
      return;
    }
    // reflect the default in the URL when no param is present (e.g. arriving from a sidebar link)
    if (!writeDefaultToUrl) return;
    const next = new URLSearchParams(window.location.search);
    next.set(param, defaultTabId);
    setSearchParams(next, { replace: true });
  }, [
    active,
    writeDefaultToUrl,
    correctInvalidToDefault,
    matched,
    raw,
    param,
    defaultTabId,
    setSearchParams
  ]);

  return [activeTabId, setActiveTabId];
}
